import { useEffect, useState } from "react";
import { api, daysAgo, today } from "@/lib/api";
import { Btn, Card, DataTable, Field, Select } from "@/components/shared";
import { Chip, money, call, when, Modal, MemberPicker, EvidenceInput, EvidenceLink, ExportBtn } from "./common";

const KINDS = [["wallet_credit", "Wallet credit"], ["wallet_debit", "Wallet debit"], ["vendor_credit", "Vendor ledger credit"], ["vendor_debit", "Vendor ledger debit"]];

export function AdjustDialog({ open, item, meta, onClose, onDone }) {
  const blank = { kind: "wallet_credit", wallet: "main", amount: "", vendor_id: "", reason: "", evidence: "" };
  const [f, setF] = useState(blank);
  const [member, setMember] = useState(null);
  useEffect(() => {
    if (!open) return;
    setF({ ...blank, kind: item?.vendor_id && !item?.user_id ? "vendor_credit" : "wallet_credit", amount: item ? String(Math.abs(item.diff || item.amount)) : "", vendor_id: item?.vendor_id || "" });
    setMember(item?.user_id ? { id: item.user_id, label: item.login_id } : null);
  }, [open, item]); // eslint-disable-line
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const wallet = f.kind.startsWith("wallet");
  const save = async () => {
    const r = await call(() => api.post("/admin/recon/adjustments", { ...f, amount: Number(f.amount), user_id: member?.id || "", item_id: item?.id || "" }), "Adjustment sent for main Admin approval");
    if (r) onDone();
  };
  return (
    <Modal open={open} onClose={onClose} title={item ? `Adjustment for ${item.ref}` : "New adjustment"} testid="adjust-dialog">
      <div className="space-y-3">
        <Field label="Type"><Select options={KINDS.map(([value, label]) => ({ value, label }))} value={f.kind} onChange={set("kind")} data-testid="adjust-kind" /></Field>
        {wallet ? (
          <div className="flex gap-3"><MemberPicker value={member} onChange={setMember} testid="adjust-member" />
            <Field label="Wallet"><Select options={[{ value: "main", label: "Main" }, { value: "aeps", label: "AEPS" }]} value={f.wallet} onChange={set("wallet")} data-testid="adjust-wallet" /></Field></div>
        ) : (
          <Field label="Vendor"><Select options={[{ value: "", label: "Select" }, ...meta.vendors.map((v) => ({ value: v.id, label: v.name }))]} value={f.vendor_id} onChange={set("vendor_id")} data-testid="adjust-vendor" /></Field>
        )}
        <Field label="Amount ₹"><input type="number" className="np-input font-mono" value={f.amount} onChange={set("amount")} data-testid="adjust-amount" /></Field>
        <Field label="Reason"><textarea className="np-input" value={f.reason} onChange={set("reason")} data-testid="adjust-reason" /></Field>
        <EvidenceInput value={f.evidence} onChange={(v) => setF({ ...f, evidence: v })} testid="adjust-evidence" />
        <Btn onClick={save} disabled={!(Number(f.amount) > 0) || f.reason.length < 5 || f.evidence.length < 3 || (wallet ? !member : !f.vendor_id)} data-testid="adjust-submit">Submit for approval</Btn>
        <div className="text-[11px] text-slate-500">Maker-checker: nothing is posted until the main Admin approves in a separate step. Posted adjustments are linked ledger entries; the ledger itself is never edited.</div>
      </div>
    </Modal>
  );
}

function DecideDialog({ a, onClose, onDone }) {
  const [remarks, setRemarks] = useState("");
  useEffect(() => setRemarks(""), [a]);
  const go = async (action) => { if (await call(() => api.post(`/admin/recon/adjustments/${a.adj_id}/decide`, { action, remarks }), action === "approve" ? "Approved and posted" : "Rejected")) onDone(); };
  return (
    <Modal open={!!a} onClose={onClose} title={`Decide · ${a?.adj_id || ""}`} testid="decide-dialog">
      {a && <div className="space-y-3 text-sm">
        <div className="rounded-md bg-slate-50 p-3 text-xs"><b>{a.kind.replace("_", " ")}</b> {money(a.amount)} · {a.login_id || a.vendor_id} {a.wallet && a.kind.startsWith("wallet") ? `(${a.wallet})` : ""}
          <div className="mt-1">{a.reason}</div><div className="mt-1">Maker: {a.maker} ({a.maker_role}) · {when(a.created_at)}</div><EvidenceLink ev={a.evidence} /></div>
        <Field label="Checker remarks"><input className="np-input" value={remarks} onChange={(e) => setRemarks(e.target.value)} data-testid="decide-remarks" /></Field>
        <div className="flex gap-2">
          <Btn onClick={() => go("approve")} disabled={remarks.length < 3} data-testid="decide-approve">Approve & post</Btn>
          <Btn variant="outline" onClick={() => go("reject")} disabled={remarks.length < 3} data-testid="decide-reject">Reject</Btn>
        </div>
      </div>}
    </Modal>
  );
}

export function Adjustments({ meta }) {
  const [status, setStatus] = useState("PENDING_APPROVAL");
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [dec, setDec] = useState(null);
  const load = () => api.get("/admin/recon/adjustments", { params: { status: status || undefined } }).then(({ data }) => setRows(data));
  useEffect(() => { load(); }, [status]); // eslint-disable-line
  const cols = [
    { label: "Adj ID", key: "adj_id", render: (r) => <span className="font-mono text-xs">{r.adj_id}</span> },
    { label: "Type", key: "kind" }, { label: "Target", csv: (r) => r.login_id || r.vendor_id, render: (r) => <span className="font-mono text-xs">{r.login_id || r.vendor_id}</span> },
    { label: "Amount", key: "amount", render: (r) => <span className="font-mono">{money(r.amount)}</span> },
    { label: "Reason", key: "reason", render: (r) => <div className="max-w-[260px] whitespace-normal text-xs">{r.reason}</div> },
    { label: "Evidence", key: "evidence", render: (r) => <EvidenceLink ev={r.evidence} /> },
    { label: "Item", key: "item_id", render: (r) => (r.item_id ? "Linked" : "—") },
    { label: "Maker", key: "maker" }, { label: "Checker", key: "checker", render: (r) => r.checker || "—" },
    { label: "Status", key: "status", render: (r) => <Chip s={r.status} testid={`adj-status-${r.adj_id}`} /> },
    { label: "Created", key: "created_at", render: (r) => <span className="text-xs">{when(r.created_at)}</span> },
    { label: "Action", render: (r) => r.status === "PENDING_APPROVAL" && (meta.is_main_admin
      ? <button className="text-xs font-semibold text-blue-700" onClick={() => setDec(r)} data-testid={`adj-decide-${r.adj_id}`}>Review</button>
      : <span className="text-[11px] text-slate-400">Awaiting main Admin</span>) },
  ];
  return (
    <div>
      <div className="np-card mb-4 flex flex-wrap items-end gap-3 p-4">
        <Field label="Status"><Select options={[["PENDING_APPROVAL", "Pending approval"], ["APPROVED", "Approved"], ["REJECTED", "Rejected"], ["", "All"]].map(([value, label]) => ({ value, label }))} value={status} onChange={(e) => setStatus(e.target.value)} data-testid="adj-status-filter" /></Field>
        <Btn onClick={() => setOpen(true)} data-testid="adj-new">New adjustment</Btn>
        <div className="ml-auto"><ExportBtn name="recon-adjustments" rows={rows} cols={cols} /></div>
      </div>
      <DataTable testid="adjustments-table" rows={rows} columns={cols} empty="No adjustments" />
      <AdjustDialog open={open} meta={meta} onClose={() => setOpen(false)} onDone={() => { setOpen(false); load(); }} />
      <DecideDialog a={dec} onClose={() => setDec(null)} onDone={() => { setDec(null); load(); }} />
    </div>
  );
}

export function ReconAudit() {
  const [f, setF] = useState({ q: "", date_from: daysAgo(30), date_to: today() });
  const [rows, setRows] = useState([]);
  const load = () => api.get("/admin/recon/audit", { params: f }).then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []); // eslint-disable-line
  const cols = [
    { label: "When", key: "at", render: (r) => <span className="text-xs">{when(r.at)}</span> }, { label: "Action", key: "action", render: (r) => <span className="font-mono text-xs">{r.action}</span> },
    { label: "Reference", key: "ref", render: (r) => <span className="font-mono text-xs">{r.ref}</span> }, { label: "By", csv: (r) => `${r.by_login} (${r.by_role})`, render: (r) => `${r.by_login} (${r.by_role})` },
    { label: "Detail", csv: (r) => JSON.stringify(r.detail), render: (r) => <div className="max-w-[420px] whitespace-normal break-all text-[11px] text-slate-600">{JSON.stringify(r.detail)}</div> },
  ];
  return (
    <Card title="Recon audit log" action={<ExportBtn name="recon-audit" rows={rows} cols={cols} />}>
      <div className="mb-3 flex flex-wrap items-end gap-3">
        <Field label="Search"><input className="np-input w-48" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} data-testid="audit-q" /></Field>
        <Field label="From"><input type="date" className="np-input" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} /></Field>
        <Field label="To"><input type="date" className="np-input" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} /></Field>
        <Btn onClick={load} data-testid="audit-search">Search</Btn>
      </div>
      <DataTable testid="recon-audit-table" rows={rows} columns={cols} />
    </Card>
  );
}
